import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTenantNavigation } from "@/hooks/useTenantNavigation";
import AdminBackButton from "@/components/AdminBackButton";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { findEquipmentConflicts, findCrossSectionConflicts, calculateEffectiveWindows } from "@/lib/equipmentConflicts";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { LogOut, ArrowLeft, Plus, Pencil, Trash2, Truck, Clock, BarChart3, AlertTriangle, DollarSign, Lock } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";

export default function MaterialMovementsPage() {
  const { reportId } = useParams();
  const { user, logout } = useAuth();
  const { navigate } = useTenantNavigation();
  const [items, setItems] = useState([]);
  const [equipmentHours, setEquipmentHours] = useState([]);
  const [delayEvents, setDelayEvents] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationsFallback, setLocationsFallback] = useState(false);
  const [equipment, setEquipment] = useState([]);
  const [contractorRates, setContractorRates] = useState([]);
  const [report, setReport] = useState(null);
  const [reportSite, setReportSite] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ 
    equipment_id: "", 
    material_id: "", 
    from_location_id: "", 
    to_location_id: "", 
    start_time: "", 
    end_time: "", 
    qty_type: "", 
    quantity: "", 
    loads: "", 
    notes: "",
    rate_id: "",
    rate_amount: 0,
    rate_description: "",
    tare_weight_lb: "",
    loaded_weight_lb: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState("table");
  const [autoFilled, setAutoFilled] = useState(false);
  const [effectiveInfo, setEffectiveInfo] = useState(null);

  // Auto-fill effective time from Equipment Hours minus Delays
  const handleEquipmentChange = (equipId) => {
    const updatedForm = { ...form, equipment_id: equipId };
    setEffectiveInfo(null);

    // Auto-fill tare weight from the equipment's saved default (if any).
    // Only overwrite when the tare field is empty so we never blow away
    // a value the operator typed by hand.
    const selectedEquip = equipment.find(e => e.id === equipId);
    if (selectedEquip && selectedEquip.tare_weight_lb && !form.tare_weight_lb) {
      updatedForm.tare_weight_lb = String(selectedEquip.tare_weight_lb);
    }

    if (!editItem && equipId) {
      const matchingEqHour = equipmentHours.find(eh => eh.equipment_id === equipId);
      if (matchingEqHour) {
        // Get delays for this equipment
        const equipDelays = delayEvents.filter(d => d.equipment_id === equipId);
        const effective = calculateEffectiveWindows(matchingEqHour, equipDelays);
        
        setEffectiveInfo(effective);
        
        // Auto-fill with the first effective window (or full hours if no delays)
        if (effective.windows.length > 0 && !form.start_time) {
          updatedForm.start_time = effective.windows[0].start;
        } else if (!form.start_time) {
          updatedForm.start_time = matchingEqHour.start_time || "";
        }
        if (effective.windows.length > 0 && !form.end_time) {
          updatedForm.end_time = effective.windows[0].end;
        } else if (!form.end_time) {
          updatedForm.end_time = matchingEqHour.end_time || "";
        }
        
        if (!form.operator_name && matchingEqHour.operator_name) {
          updatedForm.operator_name = matchingEqHour.operator_name;
        }
        setAutoFilled(true);
      }
    }
    
    setForm(updatedForm);
  };

  const loadData = async () => {
    try {
      const [entries, eqHours, delays, lookups, r] = await Promise.all([
        api.get(`/reports/${reportId}/material-movements`),
        api.get(`/reports/${reportId}/equipment-hours`),
        api.get(`/reports/${reportId}/delay-events`),
        api.get("/lookups"),
        api.get(`/reports/${reportId}`),
      ]);
      setItems(entries || []);
      setEquipmentHours(eqHours || []);
      setDelayEvents(delays || []);
      setMaterials(lookups.materials || []);
      setEquipment(lookups.equipment || []);
      setContractorRates(lookups.contractor_rates || []);
      setReport(r);
      // Filter locations by the report's site.
      // FALLBACK: if the report's site has zero site-scoped locations,
      // fall back to showing ALL tenant locations so the contractor is not
      // blocked from logging entries. Surface a warning banner so admins
      // can add proper site-scoped locations later. (Fixes "Kennard" bug.)
      const allLocations = lookups.locations || [];
      const siteScoped = allLocations.filter(
        loc => !loc.site_id || loc.site_id === r?.site_id
      );
      if (siteScoped.length === 0 && allLocations.length > 0) {
        setLocations(allLocations);
        setLocationsFallback(true);
      } else {
        setLocations(siteScoped);
        setLocationsFallback(false);
      }
      // Find the site record for display in the warning banner
      const site = (lookups.sites || []).find(s => s.id === r?.site_id);
      setReportSite(site || null);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [reportId]);

  const isReadOnly = report?.status === "Submitted";
  const getName = (list, id) => list.find(i => i.id === id)?.name || list.find(i => i.id === id)?.unit_id || "—";

  // Check for conflicts when form changes
  const conflicts = useMemo(() => {
    if (!form.equipment_id || !form.start_time || !form.end_time) return { same: [], cross: [] };
    
    const sameSection = findEquipmentConflicts(form, items, editItem?.id, equipment);
    const crossSection = findCrossSectionConflicts(form, equipmentHours, items, 'material', editItem?.id, equipment);
    
    return { same: sameSection, cross: crossSection };
  }, [form, items, equipmentHours, editItem, equipment]);

  const hasConflicts = conflicts.same.length > 0 || conflicts.cross.length > 0;

  // Find all conflicts for display in the table
  const itemConflicts = useMemo(() => {
    const conflictMap = {};
    items.forEach(item => {
      if (!item.equipment_id) return; // Skip items without equipment
      const others = items.filter(i => i.id !== item.id);
      const sameConflicts = findEquipmentConflicts(item, others, null, equipment);
      const crossConflicts = findCrossSectionConflicts(item, equipmentHours, items, 'material', item.id, equipment);
      if (sameConflicts.length > 0 || crossConflicts.length > 0) {
        conflictMap[item.id] = { same: sameConflicts, cross: crossConflicts };
      }
    });
    return conflictMap;
  }, [items, equipmentHours, equipment]);

  const openAdd = () => {
    setEditItem(null);
    setAutoFilled(false);
    setEffectiveInfo(null);
    setForm({ 
      equipment_id: "", 
      material_id: "", 
      from_location_id: "", 
      to_location_id: "", 
      start_time: "", 
      end_time: "", 
      operator_name: "",
      qty_type: "", 
      quantity: "", 
      loads: "", 
      notes: "",
      rate_id: "",
      rate_amount: 0,
      rate_description: "",
      tare_weight_lb: "",
      loaded_weight_lb: "",
    });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      equipment_id: item.equipment_id || "",
      material_id: item.material_id || "",
      from_location_id: item.from_location_id || "",
      to_location_id: item.to_location_id || "",
      start_time: item.start_time || "",
      end_time: item.end_time || "",
      operator_name: item.operator_name || "",
      qty_type: item.qty_type || "",
      quantity: String(item.quantity || ""),
      loads: String(item.loads || ""),
      notes: item.notes || "",
      rate_id: item.rate_id || "",
      rate_amount: item.rate_amount || 0,
      rate_description: item.rate_description || "",
      tare_weight_lb: item.tare_weight_lb != null ? String(item.tare_weight_lb) : "",
      loaded_weight_lb: item.loaded_weight_lb != null ? String(item.loaded_weight_lb) : "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.material_id || !form.from_location_id || !form.to_location_id || !form.qty_type || !form.quantity) {
      toast.error("Material, locations, unit type, and quantity are required");
      return;
    }
    const tiedRoutes = contractorRates.filter(r => r.contractor_id === report?.contractor_id && r.material_id === form.material_id);
    if (tiedRoutes.length > 1 && !tiedRoutes.some(r => r.id === form.rate_id)) {
      toast.error("Select the route / rate card used for this material");
      return;
    }
    if (form.loads === "" || form.loads === null || parseInt(form.loads) < 1) {
      toast.error("Loads is required (enter the number of truck loads, minimum 1)");
      return;
    }
    if (!form.start_time || !form.end_time) {
      toast.error("Start time and end time are required");
      return;
    }
    if (form.start_time >= form.end_time) {
      toast.error("End time must be after start time");
      return;
    }
    
    // Show warning but allow save if there are conflicts
    if (hasConflicts) {
      toast.warning("Saving with time conflicts. Please verify the schedule.");
    }
    
    setSaving(true);
    const tareLb = parseFloat(form.tare_weight_lb) || 0;
    const loadedLb = parseFloat(form.loaded_weight_lb) || 0;
    const netLb = (tareLb > 0 && loadedLb > 0 && loadedLb > tareLb) ? +(loadedLb - tareLb).toFixed(2) : null;
    const payload = { 
      ...form, 
      quantity: parseFloat(form.quantity) || 0, 
      loads: parseInt(form.loads),
      tare_weight_lb: form.tare_weight_lb ? tareLb : null,
      loaded_weight_lb: form.loaded_weight_lb ? loadedLb : null,
      net_weight_lb: netLb,
    };
    try {
      if (editItem) {
        await api.put(`/reports/${reportId}/material-movements/${editItem.id}`, payload);
        toast.success("Entry updated");
      } else {
        await api.post(`/reports/${reportId}/material-movements`, payload);
        toast.success("Entry added");
      }
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/reports/${reportId}/material-movements/${id}`);
      toast.success("Entry deleted");
      await loadData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  // Timeline data grouped by equipment
  const timelineData = useMemo(() => {
    const byEquipment = {};
    items.forEach(item => {
      const eqId = item.equipment_id || "unassigned";
      const eqName = item.equipment_id ? getName(equipment, item.equipment_id) : "Unassigned";
      if (!byEquipment[eqId]) {
        byEquipment[eqId] = { id: eqId, name: eqName, entries: [] };
      }
      byEquipment[eqId].entries.push(item);
    });
    // Sort entries by start_time within each equipment
    Object.values(byEquipment).forEach(eq => {
      eq.entries.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
    });
    return Object.values(byEquipment);
  }, [items, equipment]);

  // Calculate time position for timeline
  const getTimePosition = (time) => {
    if (!time) return 0;
    const [h, m] = time.split(":").map(Number);
    return ((h * 60 + m) / (24 * 60)) * 100;
  };

  const getTimeWidth = (start, end) => {
    return getTimePosition(end) - getTimePosition(start);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;

  return (
    <div className="min-h-screen" data-testid="material-movements-page">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
        </div>
        <div className="flex items-center gap-4">
          <NotificationBell />
          <AdminBackButton />
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          <Button variant="ghost" size="icon" onClick={logout}><LogOut className="h-4 w-4" strokeWidth={1.5} /></Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Button variant="ghost" size="sm" className="mb-6 text-muted-foreground" onClick={() => navigate(`/report/${reportId}/dashboard`)} data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Dashboard
        </Button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-headline text-2xl font-bold tracking-tight flex items-center gap-2">
              <Truck className="h-5 w-5 text-amber-400" strokeWidth={1.5} /> Material Movements
            </h1>
            <p className="text-muted-foreground text-sm mt-1"><span className="kpi-value">{items.length}</span> entries</p>
          </div>
          <div className="flex items-center gap-2">
            {!isReadOnly && (
              <Button onClick={openAdd} data-testid="add-material-button" className="font-headline font-semibold">
                <Plus className="h-4 w-4 mr-1" strokeWidth={2} /> Add
              </Button>
            )}
          </div>
        </div>

        <Tabs value={viewMode} onValueChange={setViewMode} className="mb-6">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="table" className="data-[state=active]:bg-primary/20 data-[state=active]:text-primary">
              <BarChart3 className="h-4 w-4 mr-1" /> Table
            </TabsTrigger>
            <TabsTrigger value="timeline" className="data-[state=active]:bg-primary/20 data-[state=active]:text-primary">
              <Clock className="h-4 w-4 mr-1" /> Timeline
            </TabsTrigger>
          </TabsList>

          <TabsContent value="table" className="mt-4">
            {items.length === 0 ? (
              <Card className="border-border bg-card"><CardContent className="py-12 text-center text-muted-foreground text-sm">No material movements logged yet</CardContent></Card>
            ) : (
              <Card className="border-border bg-card overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-muted-foreground text-xs uppercase">Time</TableHead>
                      <TableHead className="text-muted-foreground text-xs uppercase">Equipment</TableHead>
                      <TableHead className="text-muted-foreground text-xs uppercase">Material</TableHead>
                      <TableHead className="text-muted-foreground text-xs uppercase">From</TableHead>
                      <TableHead className="text-muted-foreground text-xs uppercase">To</TableHead>
                      <TableHead className="text-muted-foreground text-xs uppercase">Qty</TableHead>
                      <TableHead className="text-muted-foreground text-xs uppercase">Loads</TableHead>
                      <TableHead className="text-muted-foreground text-xs uppercase">Rate</TableHead>
                      <TableHead className="text-muted-foreground text-xs uppercase text-right">Cost</TableHead>
                      <TableHead className="text-muted-foreground text-xs uppercase">Status</TableHead>
                      {!isReadOnly && <TableHead className="text-right text-muted-foreground text-xs uppercase">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => {
                      const hasItemConflict = itemConflicts[item.id];
                      return (
                      <TableRow key={item.id} className={`border-border ${hasItemConflict ? 'bg-orange-500/5' : ''}`} data-testid={`material-row-${item.id}`}>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {item.start_time && item.end_time ? `${item.start_time}-${item.end_time}` : "—"}
                        </TableCell>
                        <TableCell className="text-sm font-medium">{getName(equipment, item.equipment_id)}</TableCell>
                        <TableCell className="text-sm">{getName(materials, item.material_id)}</TableCell>
                        <TableCell className="text-sm">{getName(locations, item.from_location_id)}</TableCell>
                        <TableCell className="text-sm">{getName(locations, item.to_location_id)}</TableCell>
                        <TableCell className="kpi-value text-sm">
                          {item.quantity} {item.qty_type}
                          {item.net_weight_lb && (
                            <div className="text-[10px] text-muted-foreground font-mono mt-0.5" data-testid={`scale-badge-${item.id}`}>
                              {Number(item.net_weight_lb).toLocaleString(undefined, { maximumFractionDigits: 0 })} lb net (tare {Number(item.tare_weight_lb).toLocaleString(undefined, { maximumFractionDigits: 0 })} / gross {Number(item.loaded_weight_lb).toLocaleString(undefined, { maximumFractionDigits: 0 })})
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{item.loads || "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{item.rate_description || "—"}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{item.rate_amount && item.quantity ? `$${(item.rate_amount * item.quantity).toFixed(2)}` : "—"}</TableCell>
                        <TableCell>
                          {hasItemConflict ? (
                            <span className="inline-flex items-center gap-1 text-orange-400 text-xs" title={`Conflicts with ${hasItemConflict.same.length + hasItemConflict.cross.length} other entries`}>
                              <AlertTriangle className="h-3.5 w-3.5" /> Conflict
                            </span>
                          ) : item.equipment_id ? (
                            <span className="text-primary text-xs">OK</span>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                        {!isReadOnly && (
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(item)}><Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} /></Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                    })}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="timeline" className="mt-4">
            <Card className="border-border bg-card">
              <CardContent className="py-4">
                <div className="mb-4">
                  <h3 className="font-headline text-sm font-semibold mb-2">Shift Timeline by Equipment</h3>
                  <p className="text-xs text-muted-foreground">Visual overview of material movements throughout the shift</p>
                </div>
                
                {/* Time axis */}
                <div className="relative mb-2 border-b border-border pb-2">
                  <div className="flex justify-between text-[10px] text-muted-foreground font-mono">
                    {[0, 4, 8, 12, 16, 20, 24].map(h => (
                      <span key={h} style={{ position: 'absolute', left: `${(h/24)*100}%`, transform: 'translateX(-50%)' }}>
                        {String(h).padStart(2, '0')}:00
                      </span>
                    ))}
                  </div>
                </div>

                {/* Timeline rows by equipment */}
                <div className="space-y-3 mt-6">
                  {timelineData.length === 0 ? (
                    <p className="text-center text-muted-foreground text-sm py-8">No material movements to display</p>
                  ) : (
                    timelineData.map((eq) => (
                      <div key={eq.id} className="relative">
                        <div className="flex items-center gap-3">
                          <div className="w-28 text-xs font-medium truncate" title={eq.name}>{eq.name}</div>
                          <div className="flex-1 relative h-8 bg-background rounded border border-border">
                            {eq.entries.map((entry, idx) => {
                              const left = getTimePosition(entry.start_time);
                              const width = getTimeWidth(entry.start_time, entry.end_time);
                              const colors = {
                                "Ore": "bg-amber-500/70 border-amber-500",
                                "Waste": "bg-slate-500/70 border-slate-500",
                                "Other": "bg-blue-500/70 border-blue-500",
                              };
                              const mat = materials.find(m => m.id === entry.material_id);
                              const colorClass = colors[mat?.category] || colors["Other"];
                              
                              return (
                                <div
                                  key={entry.id}
                                  className={`absolute top-1 bottom-1 rounded border ${colorClass} flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity`}
                                  style={{ left: `${left}%`, width: `${Math.max(width, 2)}%` }}
                                  title={`${entry.start_time}-${entry.end_time}: ${getName(materials, entry.material_id)} from ${getName(locations, entry.from_location_id)} to ${getName(locations, entry.to_location_id)} (${entry.quantity} ${entry.qty_type}${entry.loads ? `, ${entry.loads} loads` : ''})`}
                                >
                                  {width > 8 && (
                                    <span className="text-[9px] text-white font-medium truncate px-1">
                                      {getName(materials, entry.material_id)}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 mt-6 pt-4 border-t border-border">
                  <span className="text-xs text-muted-foreground">Legend:</span>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-amber-500/70 border border-amber-500"></div>
                    <span className="text-xs">Ore</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-slate-500/70 border border-slate-500"></div>
                    <span className="text-xs">Waste</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-blue-500/70 border border-blue-500"></div>
                    <span className="text-xs">Other</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-card border-border max-w-lg" data-testid="material-dialog">
            <DialogHeader>
              <DialogTitle className="font-headline">{editItem ? "Edit" : "Add"} Material Movement</DialogTitle>
              <DialogDescription className="text-muted-foreground text-xs">Track material transport details and time windows</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* Equipment Selection */}
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Equipment</Label>
                <Select value={form.equipment_id} onValueChange={handleEquipmentChange}>
                  <SelectTrigger data-testid="mat-equipment-select" className="bg-background border-border"><SelectValue placeholder="Select equipment (optional)" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.unit_id} ({e.equipment_type})</SelectItem>)}
                  </SelectContent>
                </Select>
                {autoFilled && (
                  <div className="space-y-1" data-testid="autofill-hint">
                    <p className="text-[11px] text-emerald-400 flex items-center gap-1">
                      <Clock className="h-3 w-3" strokeWidth={1.5} /> Pre-filled from Equipment Hours
                    </p>
                    {effectiveInfo && effectiveInfo.delayMinutes > 0 && (
                      <div className="text-[11px] bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1.5">
                        <p className="text-amber-400 font-medium">
                          Effective: {(effectiveInfo.effectiveMinutes / 60).toFixed(1)}h
                          ({(effectiveInfo.totalMinutes / 60).toFixed(1)}h total - {(effectiveInfo.delayMinutes / 60).toFixed(1)}h delays)
                        </p>
                        {effectiveInfo.windows.length > 1 && (
                          <div className="mt-1 space-y-0.5">
                            <p className="text-muted-foreground">Available windows:</p>
                            {effectiveInfo.windows.map((w, i) => (
                              <button
                                key={i}
                                type="button"
                                className="block text-left text-emerald-400 hover:text-emerald-300 underline underline-offset-2 cursor-pointer"
                                onClick={() => setForm({ ...form, start_time: w.start, end_time: w.end })}
                              >
                                {w.start} — {w.end}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {effectiveInfo && effectiveInfo.delayMinutes === 0 && (
                      <p className="text-[11px] text-muted-foreground">
                        Full shift: {(effectiveInfo.totalMinutes / 60).toFixed(1)}h (no delays)
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Time Window */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Start Time *</Label>
                  <Input 
                    data-testid="mat-start-time" 
                    type="time" 
                    value={form.start_time} 
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })} 
                    className="bg-background border-border font-mono" 
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">End Time *</Label>
                  <Input 
                    data-testid="mat-end-time" 
                    type="time" 
                    value={form.end_time} 
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })} 
                    className="bg-background border-border font-mono" 
                  />
                </div>
              </div>

              {/* Operator Name */}
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Operator Name</Label>
                <Input data-testid="mat-operator-name" value={form.operator_name} onChange={(e) => setForm({ ...form, operator_name: e.target.value })} placeholder="Operator name" className="bg-background border-border" />
              </div>

              {/* Conflict Warning */}
              {hasConflicts && (
                <Alert className="border-orange-500/50 bg-orange-500/10">
                  <AlertTriangle className="h-4 w-4 text-orange-400" />
                  <AlertDescription className="text-orange-300 text-sm">
                    <strong>Schedule Conflict Detected!</strong>
                    <ul className="mt-2 space-y-1 text-xs">
                      {conflicts.same.map((c, i) => (
                        <li key={`same-${i}`}>
                          <strong>{c.equipmentName}</strong> already in Material Movements: {c.existingTime}
                        </li>
                      ))}
                      {conflicts.cross.map((c, i) => (
                        <li key={`cross-${i}`}>
                          <strong>{c.equipmentName}</strong> used in {c.section}: {c.existingTime}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-[10px] text-orange-400/70">You can still save, but please verify the schedule is correct.</p>
                  </AlertDescription>
                </Alert>
              )}

              {/* Material */}
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Material *</Label>
                <Select value={form.material_id} onValueChange={(v) => {
                  // Material drives the rate card: auto-fill when exactly one route is tied; operator picks when several
                  const tied = contractorRates.filter(r => r.contractor_id === report?.contractor_id && r.material_id === v);
                  const only = tied.length === 1 ? tied[0] : null;
                  setForm({ ...form, material_id: v, rate_id: only ? only.id : "", rate_amount: only ? only.rate : 0, rate_description: only ? `${only.category} - ${only.description}` : "" });
                }}>
                  <SelectTrigger data-testid="mat-material-select" className="bg-background border-border"><SelectValue placeholder="Select material" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {materials.filter(m => !m.site_id || m.site_id === report?.site_id).map((m) => <SelectItem key={m.id} value={m.id}>{m.name} ({m.category})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Locations */}
              {locationsFallback && (
                <Alert className="border-amber-500/50 bg-amber-500/10" data-testid="locations-fallback-alert">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <AlertDescription className="text-amber-300 text-xs">
                    No locations are configured for <strong>{reportSite?.name || "this site"}</strong>. Showing all available locations so you can continue. Ask your admin to add site-specific locations under <strong>Admin → Locations</strong>.
                  </AlertDescription>
                </Alert>
              )}
              {locations.length === 0 && (
                <Alert className="border-red-500/50 bg-red-500/10" data-testid="locations-empty-alert">
                  <AlertTriangle className="h-4 w-4 text-red-400" />
                  <AlertDescription className="text-red-300 text-xs">
                    No locations exist yet. Please contact your admin to add locations under <strong>Admin → Locations</strong> before logging material movements.
                  </AlertDescription>
                </Alert>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">From *</Label>
                  <Select value={form.from_location_id} onValueChange={(v) => setForm({ ...form, from_location_id: v })}>
                    <SelectTrigger data-testid="mat-from-select" className="bg-background border-border"><SelectValue placeholder={locations.length === 0 ? "No locations available" : "From"} /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {locations.length === 0 ? (
                        <div className="px-2 py-3 text-xs text-muted-foreground text-center">No locations configured</div>
                      ) : (
                        locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} ({l.location_type})</SelectItem>)
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">To *</Label>
                  <Select value={form.to_location_id} onValueChange={(v) => setForm({ ...form, to_location_id: v })}>
                    <SelectTrigger data-testid="mat-to-select" className="bg-background border-border"><SelectValue placeholder={locations.length === 0 ? "No locations available" : "To"} /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {locations.length === 0 ? (
                        <div className="px-2 py-3 text-xs text-muted-foreground text-center">No locations configured</div>
                      ) : (
                        locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} ({l.location_type})</SelectItem>)
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Truck Scale Weights (optional) — enter lb, get net + short tons instantly */}
              <div className="space-y-3 p-3 rounded border border-border bg-background/50" data-testid="truck-scale-section">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Truck Scale Weights <span className="text-xs text-muted-foreground font-normal">(optional)</span></Label>
                  <span className="text-[10px] text-muted-foreground">1 short ton = 2,000 lb</span>
                </div>
                {(() => {
                  const selectedEquip = equipment.find(e => e.id === form.equipment_id);
                  const defaultTare = selectedEquip?.tare_weight_lb;
                  const tareMatchesDefault = defaultTare && parseFloat(form.tare_weight_lb) === parseFloat(defaultTare);
                  if (!defaultTare) return null;
                  return (
                    <p className="text-[11px] text-emerald-400 flex items-center gap-1" data-testid="tare-prefilled-hint">
                      <Clock className="h-3 w-3" strokeWidth={1.5} />
                      {tareMatchesDefault
                        ? <>Tare prefilled from <strong>{selectedEquip.unit_id}</strong> ({Number(defaultTare).toLocaleString()} lb). Just enter the loaded weight.</>
                        : <>This truck has a saved tare of {Number(defaultTare).toLocaleString()} lb — <button type="button" className="underline underline-offset-2" onClick={() => setForm({ ...form, tare_weight_lb: String(defaultTare) })}>use it</button>.</>
                      }
                    </p>
                  );
                })()}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Tare (empty truck) lb</Label>
                    <Input
                      data-testid="mat-tare-weight"
                      type="number" step="1" min="0"
                      value={form.tare_weight_lb}
                      onChange={(e) => setForm({ ...form, tare_weight_lb: e.target.value })}
                      placeholder="e.g. 32,500"
                      className="bg-background border-border font-mono text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Loaded (gross) lb</Label>
                    <Input
                      data-testid="mat-loaded-weight"
                      type="number" step="1" min="0"
                      value={form.loaded_weight_lb}
                      onChange={(e) => setForm({ ...form, loaded_weight_lb: e.target.value })}
                      placeholder="e.g. 78,300"
                      className="bg-background border-border font-mono text-sm"
                    />
                  </div>
                </div>
                {(() => {
                  const tare = parseFloat(form.tare_weight_lb) || 0;
                  const loaded = parseFloat(form.loaded_weight_lb) || 0;
                  if (!tare || !loaded) return null;
                  if (loaded <= tare) {
                    return <p className="text-xs text-red-400" data-testid="scale-error">Loaded weight must be greater than tare weight.</p>;
                  }
                  const netLb = loaded - tare;
                  const netTons = netLb / 2000;
                  const canUseAsQty = form.qty_type === "Tons" || form.qty_type === "";
                  return (
                    <div className="flex items-center justify-between gap-3 pt-1" data-testid="scale-computed">
                      <div className="text-sm">
                        <span className="text-muted-foreground text-xs">Net load: </span>
                        <span className="font-mono font-semibold">{netLb.toLocaleString(undefined, { maximumFractionDigits: 0 })} lb</span>
                        <span className="text-muted-foreground mx-1.5">·</span>
                        <span className="font-mono font-semibold text-emerald-400">{netTons.toFixed(2)} short tons</span>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setForm({ ...form, qty_type: "Tons", quantity: netTons.toFixed(2) })}
                        data-testid="use-net-as-quantity"
                        className="h-7 text-xs whitespace-nowrap"
                        disabled={!canUseAsQty && form.quantity === netTons.toFixed(2)}
                      >
                        Use as Quantity (Tons)
                      </Button>
                    </div>
                  );
                })()}
              </div>

              {/* Quantity */}
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Unit Type *</Label>
                <Select value={form.qty_type} onValueChange={(v) => setForm({ ...form, qty_type: v })}>
                  <SelectTrigger data-testid="mat-unit-select" className="bg-background border-border"><SelectValue placeholder="Select unit" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="Tons">Tons</SelectItem>
                    <SelectItem value="CYD">CYD (Cubic Yards)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Quantity *</Label>
                  <Input data-testid="mat-quantity" type="number" step="0.01" min="0" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="bg-background border-border font-mono" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Loads *</Label>
                  <Input data-testid="mat-loads" type="number" min="1" step="1" value={form.loads} onChange={(e) => setForm({ ...form, loads: e.target.value })} className="bg-background border-border font-mono" placeholder="e.g. 4" />
                </div>
              </div>

              {/* Rate Card — driven by the selected material: locked if one route, pick if several */}
              {form.material_id && (() => {
                const tied = contractorRates.filter(r => r.contractor_id === report?.contractor_id && r.material_id === form.material_id);
                const savedRate = form.rate_id ? contractorRates.find(r => r.id === form.rate_id) : null;
                const options = tied.length ? tied : (savedRate ? [savedRate] : []);
                const rate = options.find(r => r.id === form.rate_id) || (options.length === 1 ? options[0] : null);
                const computedCost = rate && form.quantity ? (parseFloat(form.quantity) * rate.rate).toFixed(2) : null;
                if (options.length === 0) {
                  return (
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Rate Card</Label>
                      <Alert className="border-amber-500/30 bg-amber-500/10 py-2" data-testid="mat-no-rate-alert">
                        <AlertDescription className="text-xs text-amber-400">
                          No rate configured for this material — contact your admin. The movement will be saved without a rate ($0).
                        </AlertDescription>
                      </Alert>
                    </div>
                  );
                }
                return (
                  <div className="space-y-2">
                    {options.length === 1 ? (
                      <>
                        <Label className="text-sm text-muted-foreground flex items-center gap-1.5">Rate Card <Lock className="h-3 w-3" strokeWidth={1.5} /><span className="text-[10px]">locked by material</span></Label>
                        <div className="px-3 py-2 rounded bg-background border border-border text-sm" data-testid="mat-rate-locked">
                          {rate.category} — {rate.description} <span className="font-mono text-muted-foreground">(${parseFloat(rate.rate).toFixed(2)}/{rate.unit})</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <Label className="text-sm text-muted-foreground">Route / Rate Card *</Label>
                        <Select value={form.rate_id} onValueChange={(v) => {
                          const r = options.find(x => x.id === v);
                          if (r) setForm({ ...form, rate_id: v, rate_amount: r.rate, rate_description: `${r.category} - ${r.description}` });
                        }}>
                          <SelectTrigger data-testid="mat-rate-select" className="bg-background border-border">
                            <SelectValue placeholder={`Select route (${options.length} routes for this material)`} />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border">
                            {options.map((r) => (
                              <SelectItem key={r.id} value={r.id}>
                                {r.category} — {r.description} (${parseFloat(r.rate).toFixed(2)}/{r.unit})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[10px] text-muted-foreground">This material is hauled on several routes — pick the one used for this movement.</p>
                      </>
                    )}
                    {rate && computedCost && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded bg-emerald-500/10 border border-emerald-500/20">
                        <DollarSign className="h-4 w-4 text-emerald-400" strokeWidth={1.5} />
                        <span className="text-sm text-emerald-400 font-mono font-medium" data-testid="mat-computed-cost">
                          {form.quantity} {form.qty_type || "units"} × ${parseFloat(rate.rate).toFixed(2)}/{rate.unit} = ${computedCost}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Notes</Label>
                <Input data-testid="mat-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" className="bg-background border-border" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-material-button" className={`font-headline font-semibold ${hasConflicts ? 'bg-orange-500 hover:bg-orange-600' : ''}`}>
                {saving ? "Saving..." : hasConflicts ? "Save Anyway" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
